/**
 * What a company's request for more would let it see — the body every web
 * surface of a request shares: Home's dialog, the bell's dialog and the
 * email link's page. (D2, 4 Oct 2026)
 *
 * The lists are the SERVER's (`willSee`, `adds`, `wontSee` on the request): the
 * citizen is shown exactly what Share grants and what it does not, never a
 * client's idea of it. What Share adds is marked "New".
 */
import { Check, Lock } from '@tonnie/assets';
import { formatList, useTranslation } from '@tonnie/locale';
import { consentScopeLabel, testitTestLabel } from '@tonnie/onboarding';
import type { MyShareRequest } from '@tonnie/sdk';
import { Badge } from '@tonnie/ui';

export function ShareRequestReview({
  request,
  company,
}: {
  request: MyShareRequest;
  company: string;
}) {
  const { t } = useTranslation();
  const body =
    request.asks === 'tests'
      ? t('invitations.request.bodyTests')
      : request.checkupScored
        ? t('invitations.request.bodyCheckupScored')
        : t('invitations.request.bodyCheckupPending');

  return (
    <div className="space-y-4" data-testid="share-request-review" data-asks={request.asks}>
      <p className="text-sm text-muted-foreground">{body}</p>

      {request.asks === 'tests' && request.tests.length > 0 && (
        <p className="text-sm text-foreground" data-testid="share-request-tests">
          {formatList(
            request.tests.map((test) => testitTestLabel(test, t)),
            t
          )}
        </p>
      )}

      <section aria-labelledby="share-request-will-see" className="space-y-2">
        <h3 id="share-request-will-see" className="text-sm font-semibold text-foreground">
          {t('invitations.request.willSeeTitle', { company })}
        </h3>
        <ul className="space-y-1.5" data-testid="share-request-will-see">
          {request.willSee.map((scope) => (
            <li
              key={scope}
              className="flex items-center gap-2 text-sm"
              data-scope={scope}
              data-new={request.adds.includes(scope)}
            >
              <Check className="h-4 w-4 shrink-0 text-primary-accessible" aria-hidden="true" />
              <span>{consentScopeLabel(scope, t)}</span>
              {request.adds.includes(scope) && (
                <Badge variant="outline" className="text-2xs">
                  {t('invitations.request.new')}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      </section>

      {request.wontSee.length > 0 && (
        <section aria-labelledby="share-request-wont-see" className="space-y-2">
          <h3 id="share-request-wont-see" className="text-sm font-semibold text-foreground">
            {t('invitations.request.wontSeeTitle', { company })}
          </h3>
          <ul className="space-y-1.5" data-testid="share-request-wont-see">
            {request.wontSee.map((scope) => (
              <li
                key={scope}
                className="flex items-center gap-2 text-sm text-muted-foreground"
                data-scope={scope}
              >
                <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{consentScopeLabel(scope, t)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
